import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import React from 'react';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import * as validators from '../../../util/validators';

import {
  CustomExtendedDataField,
  FieldCheckbox,
  FieldTextInput,
  Form,
  Heading,
  PrimaryButton,
} from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { P } from '../../PageBuilder/Primitives/P';

import css from './SignupForm.module.css';

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType, terms, email, password } = values || {};

      // firstName
      const firstNameRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
      );

      // lastName
      const lastNameRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
      );

      const config = useConfiguration();

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const isTermsLeft = email && password && !terms;

      const showTooltip = submitDisabled && isTermsLeft;

      const tooltipWrapperClasses = classNames(css.buttonWrapper, {
        [css.tooltip]: showTooltip,
      });

      return (
        <Form id="signup-form" className={classes} onSubmit={handleSubmit}>
          <Heading as="h3" className={css.buyerTitle}>
            <FormattedMessage id="SignupForm.buyerTitle" />
          </Heading>

          <P className={css.buyerSubtitle}>
            <FormattedMessage id="SignupForm.buyerSubtitle" />
          </P>

          <Heading as="h3" rootClassName={css.heading}>
            <FormattedMessage id="SignupForm.credentialsLabel" />
          </Heading>

          <FieldTextInput
            type="text"
            id={formId ? `${formId}.fname` : 'fname'}
            name="fname"
            autoComplete="given-name"
            label={intl.formatMessage({ id: 'SignupForm.firstNameLabelReq' })}
            placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
            validate={firstNameRequired}
          />

          <FieldTextInput
            className={css.field}
            type="text"
            id={formId ? `${formId}.lname` : 'lname'}
            name="lname"
            autoComplete="family-name"
            label={intl.formatMessage({ id: 'SignupForm.lastNameLabelReq' })}
            placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
            validate={lastNameRequired}
          />

          <FieldTextInput
            className={css.field}
            type="email"
            id={formId ? `${formId}.email` : 'email'}
            name="email"
            autoComplete="email"
            label={intl.formatMessage({
              id: 'SignupForm.emailLabelReq',
            })}
            placeholder={intl.formatMessage({
              id: 'SignupForm.emailPlaceholder',
            })}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />

          <FieldTextInput
            className={css.password}
            type="password"
            id={formId ? `${formId}.password` : 'password'}
            name="password"
            autoComplete="new-password"
            label={intl.formatMessage({
              id: 'SignupForm.passwordLabelReq',
            })}
            placeholder={intl.formatMessage({
              id: 'SignupForm.passwordPlaceholder',
            })}
            validate={passwordValidators}
          />

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <FieldCheckbox
              textClassName={css.finePrint}
              name="isSubscribedToNewsletter"
              id={`${formId}.isSubscribedToNewsletter`}
              label={intl.formatMessage(
                { id: 'General.subscribeToNewsletter' },
                { marketplaceName: config.marketplaceName }
              )}
            />

            <div className={tooltipWrapperClasses}>
              <PrimaryButton
                type="submit"
                className={css.button}
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="SignupForm.signUp" />
              </PrimaryButton>

              {showTooltip && (
                <span className={css.tooltiptext}>
                  <FormattedMessage id="AuthenticationPage.termsAndConditionsAcceptRequired" />
                </span>
              )}
            </div>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
